import { useState } from "react";

import { SourcingFunnelCards } from "@jobs/sourced/SourcingFunnelCards";
import { SourcingResumeBatchDialog } from "@jobs/sourced/SourcingResumeBatchDialog";
import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";

import { useGetExternalSourcingFunnel } from "@/hooks/queries/useGetExternalSourcingFunnel";
import { useGetInternalSourcingFunnel } from "@/hooks/queries/useGetInternalSourcingFunnel";
import { useGetZipRecruiterSourcingFunnel } from "@/hooks/queries/useGetZipRecruiterSourcingFunnel";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import {
  EXTERNAL_SOURCING_NO_TICKET_MESSAGE,
  SOURCING_RESUME_BATCH_AUTHORIZED_EMAILS,
  ZIP_RECRUITER_SOURCING_NO_TICKET_MESSAGE,
} from "@/lib/constants";
import { Job } from "@/types";

// Sally works the 4-5 stars matches.
const INTERNAL_SALLY_CAPTIONS = {
  found:
    "Laborup candidates whose background makes them a great or excellent fit.",
  contacted: "Found candidates who have been contacted by Sally.",
  interested:
    "Candidates who showed interest in the role. Added to the pipeline.",
  notInterested:
    "Contacted candidates who declined, opted out, or were closed after no response.",
};

// Stevie works the 1-3 star matches.
const INTERNAL_STEVIE_CAPTIONS = {
  found: "Laborup candidates whose background makes them an okay or good fit.",
  contacted: "Found candidates who have been contacted by Stevie",
  interested:
    "Candidates who showed interest in the role, talked to Stevie, and were rated a good, great, or excellent fit after the call. Added to the pipeline.",
  notInterested:
    "Contacted candidates who declined, opted out, or were closed after no response.",
};

const EXTERNAL_FUNNEL_CAPTIONS = {
  found:
    "Candidates sourced from Indeed Smart Sourcing whose background would make them a potential fit.",
  contacted: "Found candidates who have been contacted by a team member.",
  interested:
    "Candidates who replied that they are interested. Added to applied pipeline.",
  notInterested:
    "Not tracked for Indeed Smart Sourcing — declines aren't reported back, so this is always 0.",
};

const ZIP_RECRUITER_FUNNEL_CAPTIONS = {
  found:
    "Candidates sourced from the ZipRecruiter resume database whose background would make them a potential fit.",
  contacted: "Found candidates who have been contacted by a team member.",
  interested:
    "Candidates who replied that they are interested. Added to applied pipeline.",
  notInterested:
    "Contacted candidates who declined, opted out, or were closed after no response.",
};

const EXTERNAL_ACTIVE_MESSAGE = "Candidates are being sourced";

export const JobSourced = ({ className }: { className?: string; job: Job }) => {
  const internal = useGetInternalSourcingFunnel();
  const external = useGetExternalSourcingFunnel();
  const zipRecruiter = useGetZipRecruiterSourcingFunnel();

  const currentUser = useGetCurrentUser();
  const canAddResumes = SOURCING_RESUME_BATCH_AUTHORIZED_EMAILS.includes(
    currentUser?.email.toLowerCase() ?? "",
  );

  const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false);
  // The batch runs in the background, so the only thing to show once it starts
  // is how many resumes went in. The real report arrives by email.
  const [queuedCount, setQueuedCount] = useState<number | null>(null);

  const render = () => {
    if (internal.isPending || external.isPending || zipRecruiter.isPending) {
      return <LoadingIndicator className="absolute inset-0" variant="large" />;
    }

    if (internal.isError || external.isError || zipRecruiter.isError) {
      return (
        <Center>
          <ErrorText errorMessage="Error retrieving sourcing funnel" />
        </Center>
      );
    }

    return (
      <Column className="gap-16">
        {canAddResumes && (
          <Row className="items-center justify-between">
            <Column className="gap-1">
              <Text variant="header">Add sourced resumes</Text>
              <Text variant="muted">
                {queuedCount === null
                  ? "Drop resumes you unlocked on an outside resume database and we'll rate them and start outreach."
                  : `${queuedCount.toString()} resumes queued. A summary will be emailed when the batch finishes.`}
              </Text>
            </Column>
            <Button
              onClick={() => {
                setIsBatchDialogOpen(true);
              }}
            >
              Add resumes
            </Button>
          </Row>
        )}

        <Column className="gap-3">
          <Text variant="header">Internal — Sally</Text>
          <SourcingFunnelCards
            captions={INTERNAL_SALLY_CAPTIONS}
            counts={internal.data.sally}
          />
        </Column>

        <Column className="gap-3">
          <Text variant="header">Internal — Stevie</Text>
          <SourcingFunnelCards
            captions={INTERNAL_STEVIE_CAPTIONS}
            counts={internal.data.stevie}
          />
        </Column>

        <Column className="gap-3">
          <Text variant="header">External - Indeed Smart Sourcing</Text>
          {external.data.hasTicket ? (
            <SourcingFunnelCards
              captions={EXTERNAL_FUNNEL_CAPTIONS}
              counts={external.data}
              textLabel={
                external.data.isActive ? EXTERNAL_ACTIVE_MESSAGE : undefined
              }
            />
          ) : (
            <Text variant="muted">{EXTERNAL_SOURCING_NO_TICKET_MESSAGE}</Text>
          )}
        </Column>

        <Column className="gap-3">
          <Text variant="header">External - ZipRecruiter Resume Database</Text>
          {zipRecruiter.data.hasTicket ? (
            <SourcingFunnelCards
              captions={ZIP_RECRUITER_FUNNEL_CAPTIONS}
              counts={zipRecruiter.data}
              textLabel={
                zipRecruiter.data.isActive ? EXTERNAL_ACTIVE_MESSAGE : undefined
              }
            />
          ) : (
            <Text variant="muted">
              {ZIP_RECRUITER_SOURCING_NO_TICKET_MESSAGE}
            </Text>
          )}
        </Column>
      </Column>
    );
  };

  return (
    <div className={className}>
      {render()}
      <SourcingResumeBatchDialog
        onOpenChange={setIsBatchDialogOpen}
        onSuccess={setQueuedCount}
        open={isBatchDialogOpen}
      />
    </div>
  );
};
